import React from 'react';

import { useContextSelector } from 'use-context-selector';

import ContainerPage from '../../components/ContainerPage';
import Button from '../../components/Button';

import { SpecialistContext } from '../../contexts/SpecialistContext';
import { RouterContext } from '../../contexts/RouterContext';
import { ExternalOffices, SpecialistUnit, UnitContact } from '../../models/Specialist';

const DoctorDefaultIcon = 'https://agendamento-images-nprd.telemedicinaeinstein.com.br/schedule-lib-assets/patient.svg';
const SpecialtyIcon = 'https://agendamento-images-nprd.telemedicinaeinstein.com.br/schedule-lib-assets/stethoscope.svg';
const UnitsIcon = 'https://agendamento-images-nprd.telemedicinaeinstein.com.br/schedule-lib-assets/pin-rouded.svg';
const ContactsIcon = 'https://agendamento-images-nprd.telemedicinaeinstein.com.br/schedule-lib-assets/phone-blue.svg';
const LanguagesIcon =
  'https://agendamento-images-nprd.telemedicinaeinstein.com.br/schedule-lib-assets/languages-rounded.svg';

import { BodyDetails, HeadDetails, Separator, WrapperDetail, CurriculumButton } from './styles';
import { ScheduleSpecialistBuilder } from '../../models/ScheduleSpecialist';
import { ScheduleContext } from '../../contexts/ScheduleContext';
import { PAGES } from '../../Router/pages';
import { ModuleContext } from '../../contexts/ModuleContext';
import { usePatientModule } from '../../hooks/usePatientModule';
import { PatientContext } from '../../contexts/PatientContext';
import { UnitContacts } from './components/UnitContacts';
import { UnitAddress } from './components/UnitAddress';
import { capitalize } from '../../utils/formatters';
import { handleSpecialtiesWithExpertises } from '../../utils/specialist-utils';
import { ModalsContext } from '../../contexts/ModalsContext';
import { CartContext } from '../../contexts/CartContext';
import { PaymentContext } from '../../contexts/PaymentContext';
import { PAYMENT_TYPE } from '../../utils/constants';
import { Page, useMainRouter } from '../../Router/pageComponents';
import { PlatformContext } from '../../contexts/PlatformContext';
import { ClipboardButton } from './components/ClipboardButton';
import { LoadingOverlay } from '../../components';
import { useAnalytics } from '../../hooks/useAnalytics';
import { FirebaseEvents } from '../../utils/enums/firebase-events';

export default function SpecialistDetails() {
  const { openModal } = useContextSelector(ModalsContext, state => state);
  const { clearData } = useContextSelector(PlatformContext, state => state);
  const { changeProduct } = useContextSelector(CartContext, state => state);
  const { goBack, goTo, history } = useContextSelector(RouterContext, state => state);
  const { patientSelected } = useContextSelector(PatientContext, state => state);
  const { selectItemToSchedule, logged } = useContextSelector(ModuleContext, state => state);
  const { setEligibilityPlans, eligibilityPlans } = useContextSelector(PaymentContext, state => state);
  const { selectedSpecialist, setSelectedSpecialty } = useContextSelector(SpecialistContext, state => state);

  const { addPatientList } = useContextSelector(PatientContext, state => state);
  const {
    setScheduleSpecialistSelected,
    selectedItem,
    changeSchedule,
    changeScheduleSpecialist,
    scheduleSpecialistSelected
  } = useContextSelector(ScheduleContext, state => state);

  const { handleAddPatient } = usePatientModule({
    addPatientList,
    changeProduct,
    eligibilityPlans,
    setEligibilityPlans,
    goTo,
    changeSchedule,
    selectedSchedule: selectedItem,
    openModal,
    scheduleSpecialistSelected,
    setScheduleSpecialistSelected,
    changeScheduleSpecialist
  });
  const [units, setUnits] = React.useState([] as SpecialistUnit[]);
  const [externalOfficers, setExternalOfficers] = React.useState([] as ExternalOffices[]);

  const getLanguagesString = () => {
    return selectedSpecialist?.languages?.join(', ');
  };

  const checkUnitScheduleContacts = (contact: UnitContact) => {
    if (contact.contact_type.name === 'Contato para agendamento')
      return (
        !!contact.value.landlines.length || !!contact.value.cell_phone_numbers.length || !!contact.value.others.length
      );

    return false;
  };

  const getContactValue = (unit: SpecialistUnit | ExternalOffices) => {
    if (!unit?.contacts) return null;
    return unit.contacts.find(contact => checkUnitScheduleContacts(contact));
  };

  const validateRenderUnits = () => {
    return selectedSpecialist?.units?.find(unit => getContactValue(unit));
  };

  const allowSelfSchedule = selectedSpecialist?.units.some(
    unit =>
      !!unit.schedule_types?.some(type => type.name === 'Autoagendamento') && !!unit?.external_platform?.code_company
  );

  function handleAddress(externalOfficer: ExternalOffices) {
    const { address, number, neighborhood, city, federated_state } = externalOfficer;

    const addressParts: string[] = [];
    if (address) addressParts.push(address);
    if (number) addressParts.push(String(number));
    if (neighborhood) addressParts.push(neighborhood);
    if (city && federated_state) addressParts.push(`${city} - ${federated_state}`);

    return addressParts.join(', ');
  }

  React.useEffect(() => {
    if (selectedSpecialist) {
      const internalOfficersFiltered = selectedSpecialist.units.filter(item => {
        if (item?.id && getContactValue(item)) return item;
      });

      const externalOfficersFiltered = selectedSpecialist.external_offices.filter(item => {
        if (item?.id && getContactValue(item)) return item;
      });

      const externalOfficesMapped = externalOfficersFiltered.map(item => ({
        ...item,
        full_address: handleAddress(item)
      }));

      setUnits(internalOfficersFiltered);
      setExternalOfficers(externalOfficesMapped);
    }
  }, [selectedSpecialist]);

  const { goBack: mainRouterGoBack, history: mainHistory, navigate } = useMainRouter();
  const { logEvent } = useAnalytics();

  return (
    <ContainerPage
      childrenFooter={
        <>
          {allowSelfSchedule && (
            <Button
              title="Agendar online"
              onClick={async () => {
                logEvent({
                  eventName: FirebaseEvents.schedule_specialist_schedule,
                  eventParams: {
                    specialist_id: selectedSpecialist?.id ?? '',
                    specialist_name: selectedSpecialist?.name ?? ''
                  }
                });

                if (selectedSpecialist) {
                  selectItemToSchedule('SPECIALIST');
                  if (logged && patientSelected)
                    await handleAddPatient({
                      patient: patientSelected,
                      itemToSchedule: 'SPECIALIST',
                      specialist: selectedSpecialist
                    });
                  if (!logged) {
                    const scheduleSpecialist = ScheduleSpecialistBuilder();

                    scheduleSpecialist.setPayment({ type: PAYMENT_TYPE.PARTICULAR });
                    setScheduleSpecialistSelected(
                      scheduleSpecialist.setSpecialist({
                        specialityList: selectedSpecialist.specialties,
                        image: selectedSpecialist.profile_picture,
                        interval: 30
                      })
                    );
                    goTo(PAGES.PATIENT_FORM_SPECIALIST.id);
                  }
                }
              }}
            />
          )}

          <Button
            title="Voltar"
            tertiary
            onClick={() => {
              clearData();
              if (history.length === 1) {
                if (mainHistory.length === 1) {
                  navigate(Page.SERVICE_LIST);
                } else mainRouterGoBack();
              } else goBack();
            }}
          />
        </>
      }
    >
      <HeadDetails>
        <img
          id="specialist-avatar"
          src={selectedSpecialist?.profile_picture ? selectedSpecialist?.profile_picture : DoctorDefaultIcon}
          alt="Specialist Avatar"
        />
        <h1 id="specialist-name">{selectedSpecialist?.name}</h1>
        {selectedSpecialist?.professional_codes?.map(code => (
          <p key={code.value} id="specialist-code">
            {code.type} {code.value}
          </p>
        ))}
        {allowSelfSchedule && selectedSpecialist && <ClipboardButton selectedSpecialist={selectedSpecialist} />}
        {selectedSpecialist?.curriculum && (
          <CurriculumButton
            onClick={() => {
              logEvent({
                eventName: FirebaseEvents.schedule_open_lattes,
                eventParams: {
                  specialist_id: selectedSpecialist.id,
                  specialist_name: selectedSpecialist.name
                }
              });

              window.open(selectedSpecialist.curriculum);
            }}
          >
            <p>Ver currículo</p>
          </CurriculumButton>
        )}
      </HeadDetails>

      <BodyDetails>
        {selectedSpecialist?.specialties.length ? (
          <>
            <WrapperDetail>
              <img id="detail-icon" src={SpecialtyIcon} alt="Detail Icon" />
              <div>
                <p id="detail-name">Especialidades</p>
                <p id="detail-description">{handleSpecialtiesWithExpertises(selectedSpecialist?.specialties || [])}</p>
              </div>
            </WrapperDetail>
          </>
        ) : null}

        {units?.length && validateRenderUnits() ? (
          <>
            <Separator />
            <WrapperDetail>
              <img id="detail-icon" src={ContactsIcon} alt="Detail Icon" />
              <div>
                <p id="detail-name">Contato</p>
                {units.map((unit, index) => (
                  <UnitContacts
                    key={index}
                    unit={unit}
                    onClickOnContact={() =>
                      logEvent({
                        eventName: FirebaseEvents.schedule_open_specialist_contact,
                        eventParams: {
                          specialist_id: selectedSpecialist?.id ?? '',
                          specialist_name: selectedSpecialist?.name ?? '',
                          unit_id: unit.id,
                          unit_name: unit.name
                        }
                      })
                    }
                  />
                ))}
              </div>
            </WrapperDetail>
          </>
        ) : externalOfficers.length ? (
          <>
            <Separator />
            <WrapperDetail>
              <img id="detail-icon" src={ContactsIcon} alt="Detail Icon" />
              <div>
                <p id="detail-name">Contato</p>
                {externalOfficers.map((office, index) => (
                  <UnitContacts
                    key={index}
                    unit={office}
                    onClickOnContact={() =>
                      logEvent({
                        eventName: FirebaseEvents.schedule_open_specialist_contact,
                        eventParams: {
                          specialist_id: selectedSpecialist?.id ?? '',
                          specialist_name: selectedSpecialist?.name ?? '',
                          unit_id: office.id,
                          unit_name: office.name
                        }
                      })
                    }
                  />
                ))}
              </div>
            </WrapperDetail>
          </>
        ) : null}

        {units?.length && validateRenderUnits() ? (
          <>
            <Separator />
            <WrapperDetail>
              <img id="detail-icon" src={UnitsIcon} alt="Detail Icon" />
              <div>
                <p id="detail-name">Unidades de atendimento</p>
                {units.map((unit, index) => (
                  <UnitAddress key={index} unit={unit} />
                ))}
              </div>
            </WrapperDetail>
          </>
        ) : externalOfficers.length ? (
          <>
            <Separator />
            <WrapperDetail>
              <img id="detail-icon" src={UnitsIcon} alt="Detail Icon" />
              <div>
                <p id="detail-name">Unidades de atendimento</p>
                {externalOfficers.map((office, index) => (
                  <UnitAddress key={index} unit={office} />
                ))}
              </div>
            </WrapperDetail>
          </>
        ) : null}

        {selectedSpecialist?.languages?.length && (
          <>
            <Separator />
            <WrapperDetail>
              <img id="detail-icon" src={LanguagesIcon} alt="Detail Icon" />
              <div>
                <p id="detail-name">Idiomas</p>
                <p id="detail-description">{capitalize(getLanguagesString() || '')}</p>
              </div>
            </WrapperDetail>
          </>
        )}
      </BodyDetails>
      <LoadingOverlay loading={!selectedSpecialist?.id} />
    </ContainerPage>
  );
}
